<script setup lang="ts">
import Hls from 'hls.js';
import { VideoType } from './types';

interface Props {
	src: string;
	isPlaying: boolean;
	poster?: string;
	videoType?: VideoType;
	productId: number;
	stockStatus: string;
}
const props = defineProps<Props>();
const video = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;
const totalWatchTime = ref(0);
const lastTime = ref(0);

const { $eventClient } = useNuxtApp();

const preparePlayer = () => {
	if (hls) hls.destroy();
	hls = new Hls();
	hls.loadSource(props.src);
	if (video.value) hls.attachMedia(video.value);
};

onMounted(() => {
	props.videoType === VideoType.HLS && preparePlayer();
	if (video.value) {
		video.value.addEventListener('play', onPlay);
		video.value.addEventListener('timeupdate', onTimeUpdate);
		video.value.addEventListener('pause', onEnded);
	}
});
onUnmounted(() => {
	hls?.destroy();
});

const onPlay = () => {
	lastTime.value = video.value?.currentTime || 0;
	$eventClient.sendEvent('video-played', {
		productId: props.productId,
		videoUrl: video.value?.src,
		totalDuration: video.value?.duration,
		stockStatus: props.stockStatus,
	});
};

const onTimeUpdate = () => {
	if (!video.value) return;

	const currentTime = video.value.currentTime;
	const delta = currentTime - lastTime.value;

	if (delta > 0) {
		totalWatchTime.value += delta;
		lastTime.value = currentTime;
	}
};

const onEnded = () => {
	$eventClient.sendEvent('video-watch-time', {
		watchedTime: totalWatchTime.value.toFixed(3),
		totalDuration: video.value?.duration,
		no_of_times_played: video.value?.duration
			? Number((totalWatchTime.value / video?.value?.duration).toFixed(3))
			: 0,
		productId: props.productId,
		videoUrl: video.value?.src,
		stockStatus: props.stockStatus,
	});
};

watch(
	() => props.isPlaying,
	() => {
		if (props.isPlaying) video.value?.play();
		else video.value?.pause();
	},
);
</script>

<template>
	<video
		ref="video"
		class="size-full"
		autoplay
		controls
		disablePictureInPicture
		loop
		muted
		playsinline
		controlslist="nodownload noremoteplayback noplaybackrate"
		:poster
		:src
	></video>
</template>
